/**
 * API 接口配置文件
 * 集中管理所有后端接口路径
 */

const API_BASE = {
  AUTH: '/account',
  STUDY: '/api/study',
  MANAGEMENT: '/api/management',
  SOUND: '/api/sound',
  WORD: '/api/word',
  IMAGE_POSITION: '/api/imageposition',
  QR_LISTENING: '/api/qrlistening',
  AUTH_CODE: '/api/AuthCode',
  SCHOOL: '/api/school',
  SCHOOL_REGISTRATION: '/api/schoolRegistration',
  TEL_ACCOUNT: '/api/TelAccount',
  MENU: '/api/menu'
}

const api = {
  // ==================== 认证相关 ====================
  /**
   * 用户登录 (MVC 接口，使用 form-urlencoded 格式)
   * POST /account/login
   * @param {string} account - 手机号
   * @param {string} pwd - 密码
   */
  login: `${API_BASE.AUTH}/login`,

  /**
   * 用户注册
   * POST /account/CreateUser
   * @param {string} ValidateCode - 手机号（验证码验证后的账号）
   * @param {string} Pwd - 密码
   * @param {string} Code - 授权码
   */
  register: `${API_BASE.AUTH}/CreateUser`,

  /**
   * 发送注册验证码
   * POST /account/CreateCode
   * @param {string} account - 手机号
   */
  sendRegisterCode: `${API_BASE.AUTH}/CreateCode`,

  /**
   * 验证注册验证码
   * POST /account/AuthCode
   * @param {string} code - 验证码
   */
  validateRegisterCode: `${API_BASE.AUTH}/AuthCode`,

  /**
   * 验证登录状态
   * POST /api/auth/check
   */
  authCheck: `/api/auth/check`,

  /**
   * 发送密码重置验证码
   * POST /account/CreateCode
   * @param {string} account - 手机号
   */
  sendPasswordResetCode: `${API_BASE.AUTH}/CreateCode`,

  /**
   * 验证密码重置验证码
   * POST /account/AuthCode
   * @param {string} code - 验证码
   */
  validatePasswordResetCode: `${API_BASE.AUTH}/AuthCode`,

  /**
   * 重置密码
   * POST /account/PasswordReset
   * @param {string} account - 手机号
   * @param {string} code - 验证码
   * @param {string} pwd - 新密码
   */
  passwordReset: `${API_BASE.AUTH}/PasswordReset`,

  // ==================== 学习相关 ====================
  /**
   * 获取用户信息
   * POST /api/study/getuserinfo
   */
  getUserInfo: `${API_BASE.STUDY}/getuserinfo`,

  /**
   * 获取产品系列列表
   * GET /api/study/getproductseries
   */
  getProductSeries: `${API_BASE.STUDY}/getproductseries`,

  /**
   * 获取产品单元列表
   * GET /api/study/getproductunits
   * @param {number} series - 系列 ID
   * @param {number} productLevel - 产品级别
   */
  getProductUnits: (series, productLevel) =>
    `${API_BASE.STUDY}/getproductunits?series=${series}&productLevel=${productLevel}`,

  /**
   * 获取单元标题
   * GET /api/study/getunittitle
   * @param {number} series - 系列 ID
   * @param {number} level- 级别
   * @param {number} productUnit - 产品单元 ID
   */
  getUnitTitle: (series, level, productUnit) =>
    `${API_BASE.STUDY}/getunittitle?series=${series}&level=${level}&productUnit=${productUnit}`,

  /**
   * 获取产品资源列表
   * POST /api/study/getproductparts
   * @param {object} params - 查询参数
   * @param {number} params.PageIndex - 页码
   * @param {number} params.Series - 系列 IDJ
   * @param {number} params.Level - 级别
   * @param {number} params.ProductUnit - 产品单元 ID
   * @param {number} params.PageSize - 每页数量
   */
  getProductParts: `${API_BASE.STUDY}/getproductparts`,

  /**
   * 退出登录
   * POST /api/study/logout
   */
  logout: `${API_BASE.STUDY}/logout`,

  /**
   * 上报学习进度
   * POST /api/study/progress/report
   * @param {string} ProductCode - 产品编码
   * @param {number} CourseType - 课程类型 (1-8)
   * @param {number} Series - 产品系列
   * @param {number} Level - 产品级别
   * @param {number} UnitNo - 单元编号
   */
  reportProgress: `${API_BASE.STUDY}/progress/report`,

  /**
   * 查询某单元的学习进度
   * GET /api/study/progress/query
   * @param {number} series - 产品系列
   * @param {number} level - 产品级别
   * @param {number} unitNo - 单元编号
   */
  queryProgress: (series, level, unitNo) =>
    `${API_BASE.STUDY}/progress/query?series=${series}&level=${level}&unitNo=${unitNo}`,

  /**
   * 清除当前用户所有学习进度
   * POST /api/study/progress/clear
   */
  clearProgress: `${API_BASE.STUDY}/progress/clear`,

  // ==================== 后台管理相关 ====================
  /**
   * 上传文件
   * POST /api/management/upload
   */
  upload: `${API_BASE.MANAGEMENT}/upload`,

  /**
   * 创建用户
   * POST /api/management/createUser
   */
  createUser: `${API_BASE.MANAGEMENT}/createUser`,

  /**
   * 获取用户列表
   * POST /api/management/getusers
   * @param {number} PageIndex - 页码
   * @param {number} PageSize - 每页数量
   * @param {string} Account - 账号关键词
   */
  getUsers: `${API_BASE.MANAGEMENT}/getusers`,

  /**
   * 禁用用户
   * POST /api/management/disableUser
   */
  disableUser: `${API_BASE.MANAGEMENT}/disableUser`,

  /**
   * 修改用户信息
   * POST /api/management/modifyUser
   */
  modifyUser: `${API_BASE.MANAGEMENT}/modifyUser`,

  /**
   * 自动完成搜索
   * GET /api/management/autocomplete
   */
  autocomplete: (keyword) => `${API_BASE.MANAGEMENT}/autocomplete?keyword=${keyword}`,

  /**
   * 创建单元
   * POST /api/management/createunit
   */
  createUnit: `${API_BASE.MANAGEMENT}/createunit`,

  /**
   * 获取单元列表
   * POST /api/management/getunits
   */
  getUnits: `${API_BASE.MANAGEMENT}/getunits`,

  /**
   * 修改单元
   * POST /api/management/modifyunit
   */
  modifyUnit: `${API_BASE.MANAGEMENT}/modifyunit`,

  /**
   * 单元自动完成搜索
   * GET /api/management/unit/autocomplete
   */
  unitAutocomplete: (keyword) => `${API_BASE.MANAGEMENT}/unit/autocomplete?keyword=${keyword}`,

  /**
   * 下载产品
   * POST /api/management/downloadproduct
   */
  downloadProduct: `${API_BASE.MANAGEMENT}/downloadproduct`,

  /**
   * 上传产品
   * POST /api/management/uploadproduct
   */
  uploadProduct: `${API_BASE.MANAGEMENT}/uploadproduct`,

  /**
   * 禁用产品
   * POST /api/management/disableproduct
   */
  disableProduct: `${API_BASE.MANAGEMENT}/disableproduct`,

  /**
   * 获取产品级别
   * GET /api/management/getproductlevels
   */
  getProductLevels: `${API_BASE.MANAGEMENT}/getproductlevels`,

  /**
   * 获取产品单元
   * GET /api/management/getproductunits
   */
  getProductUnitsForManage: (series, productLevel) =>
    `${API_BASE.MANAGEMENT}/getproductunits?series=${series}&productLevel=${productLevel}`,

  /**
   * 上传位置信息
   * POST /api/management/uploadpostion
   */
  uploadPosition: `${API_BASE.MANAGEMENT}/uploadpostion`,

  // ==================== 音频相关 ====================
  /**
   * 获取成功 MP3
   * GET /api/sound/getSuccessMp3
   */
  getSuccessMp3: `${API_BASE.SOUND}/getSuccessMp3`,

  /**
   * 获取失败 MP3
   * GET /api/sound/getFailedMp3
   */
  getFailedMp3: `${API_BASE.SOUND}/getFailedMp3`,

  /**
   * 获取 MP3
   * POST /api/sound/getMp3
   */
  getMp3: `${API_BASE.SOUND}/getMp3`,

  /**
   * 根据级别和单元获取音频信息
   * POST /api/sound/getInfoByLevelAndUnit
   */
  getSoundInfoByLevelAndUnit: `${API_BASE.SOUND}/getInfoByLevelAndUnit`,

  /**
   * 获取难度级别
   * POST /api/sound/getDifficultyByUnit
   */
  getDifficultyByUnit: `${API_BASE.SOUND}/getDifficultyByUnit`,

  /**
   * 获取音频游戏数据
   * POST /api/sound/getSoundGameData
   */
  getSoundGameData: `${API_BASE.SOUND}/getSoundGameData`,

  /**
   * 保存分数
   * POST /api/sound/saveScore
   */
  saveScore: `${API_BASE.SOUND}/saveScore`,

  /**
   * 更新单词
   * POST /api/sound/updateword
   */
  updateWord: `${API_BASE.SOUND}/updateword`,

  /**
   * 获取单词列表
   * POST /api/sound/getwordlist
   */
  getWordList: `${API_BASE.SOUND}/getwordlist`,

  /**
   * 转换为音频
   * POST /api/sound/converttosound
   */
  convertToSound: `${API_BASE.SOUND}/converttosound`,

  /**
   * 批量生成音频
   * POST /api/sound/wordsToMp3
   */
  wordsToMp3: `${API_BASE.SOUND}/wordsToMp3`,

  /**
   * 导入 Excel
   * POST /api/sound/importexcel
   */
  importExcel: `${API_BASE.SOUND}/importexcel`,

  /**
   * 导出 Excel
   * POST /api/sound/exportexcel
   */
  exportExcel: `${API_BASE.SOUND}/exportexcel`,

  // ==================== 单词相关 ====================
  /**
   * 获取单词游戏数据
   * POST /api/word/getWordGamesData
   */
  getWordGamesData: `${API_BASE.WORD}/getWordGamesData`,

  // ==================== 图片位置相关 ====================
  /**
   * 根据产品代码获取位置
   * GET /api/imageposition/getpostionsByProductCode
   */
  getPositionsByProductCode: (productCode) =>
    `${API_BASE.IMAGE_POSITION}/getpostionsByProductCode?productCode=${productCode}`,

  // ==================== 二维码听力相关 ====================
  /**
   * 获取学生二维码学习信息
   * GET /api/qrlistening/getqrstduy
   */
  getQrStudy: (productCode) => `${API_BASE.QR_LISTENING}/getqrstduy?productCode=${productCode}`,

  /**
   * 根据级别获取列表
   * GET /api/qrlistening/getbylevel
   */
  getQrListeningByLevel: (level) => `${API_BASE.QR_LISTENING}/getbylevel?level=${level}`,

  /**
   * 获取产品级别（二维码听力）
   * GET /api/qrlistening/getProductLevels
   */
  getQrProductLevels: `${API_BASE.QR_LISTENING}/getProductLevels`,

  /**
   * 获取听力列表
   * POST /api/qrlistening/getListenings
   */
  getQrListenings: `${API_BASE.QR_LISTENING}/getListenings`,

  /**
   * 上传二维码听力
   * POST /api/qrlistening/upload
   */
  uploadQrListening: `${API_BASE.QR_LISTENING}/upload`,

  /**
   * 下载二维码听力
   * POST /api/qrlistening/download
   */
  downloadQrListening: `${API_BASE.QR_LISTENING}/download`,

  /**
   * 创建二维码
   * POST /api/qrlistening/createqr
   */
  createQr: `${API_BASE.QR_LISTENING}/createqr`,

  // ==================== 验证码相关 ====================
  /**
   * 绑定验证码
   * POST /api/authcode/bindCode
   */
  bindCode: `${API_BASE.AUTH_CODE}/bindCode`,

  /**
   * 验证验证码（教师端）
   * POST /api/AuthCode/validateCode
   */
  validateCode: `${API_BASE.AUTH_CODE}/validateCode`,

  /**
   * 验证验证码（游戏）
   * POST /api/AuthCode/validateCodeForGame
   */
  validateCodeForGame: `${API_BASE.AUTH_CODE}/validateCodeForGame`,

  /**
   * 获取验证码
   * GET /api/AuthCode/getAuthCode
   */
  getAuthCode: `${API_BASE.AUTH_CODE}/getAuthCode`,
   /**
   * 获取验证码
   * GET /api/AuthCode/getAuthCode
   */
  queryAuthCode: `${API_BASE.AUTH_CODE}/queryAuthCode`,

  // ==================== 学校相关 ====================
  /**
   * 获取学校评论素材
   * POST /api/school/getReviewMaterials
   */
  getSchoolReviewMaterials: `${API_BASE.SCHOOL}/getReviewMaterials`,

  /**
   * 获取学校权限
   * GET /api/school/authority
   */
  getSchoolAuthority: (id) => `${API_BASE.SCHOOL}/authority?id=${id}`,

  // ==================== 学校开户注册 ====================
  schoolRegisterSendCode: `${API_BASE.SCHOOL_REGISTRATION}/send-code`,
  schoolRegisterCheckUserName: (userName) => `${API_BASE.SCHOOL_REGISTRATION}/check-user-name?userName=${encodeURIComponent(userName)}`,
  schoolRegisterCheckSchoolName: (schoolName) => `${API_BASE.SCHOOL_REGISTRATION}/check-school-name?schoolName=${encodeURIComponent(schoolName)}`,
  schoolRegisterOcr: `${API_BASE.SCHOOL_REGISTRATION}/ocr`,
  schoolRegisterSubmit: `${API_BASE.SCHOOL_REGISTRATION}/register`,

  // ==================== 电话账户相关 ====================
  /**
   * 发送密码重置验证码
   * POST /api/TelAccount/send-verify-code
   * @param {string} TelAccountId - 目标手机号
   */
  sendVerifyCode: `${API_BASE.TEL_ACCOUNT}/send-verify-code`,

  /**
   * 验证验证码是否正确
   * POST /api/TelAccount/validate-verify-code
   * @param {string} TelAccountId - 手机号
   * @param {string} VerifyCode - 验证码
   */
  validateVerifyCode: `${API_BASE.TEL_ACCOUNT}/validate-verify-code`,

  /**
   * 修改密码
   * POST /api/TelAccount/change-password
   * @param {string} TelAccountId - 目标手机号
   * @param {string} NewPassword - 新密码
   * @param {string} ConfirmPassword - 确认密码
   * @param {string} VerifyCode - 验证码
   */
  changePassword: `${API_BASE.TEL_ACCOUNT}/change-password`,

  // ==================== 菜单相关 ====================
  /**
   * 获取当前用户的菜单
   * GET /api/menu/user
   */
  getUserMenus: `${API_BASE.MENU}/user`,

  /**
   * 获取当前用户的默认首页菜单
   * GET /api/menu/default
   */
  getDefaultMenu: `${API_BASE.MENU}/default`,

  /**
   * 获取菜单树（管理用）
   * GET /api/menu/tree
   */
  getMenuTree: `${API_BASE.MENU}/tree`,

  /**
   * 获取所有菜单列表（管理用）
   * GET /api/menu
   */
  getAllMenus: `${API_BASE.MENU}`,

  /**
   * 获取单个菜单
   * GET /api/menu/{id}
   */
  getMenu: (id) => `${API_BASE.MENU}/${id}`,

  /**
   * 创建菜单
   * POST /api/menu
   */
  createMenu: `${API_BASE.MENU}`,

  /**
   * 更新菜单
   * PUT /api/menu/{id}
   */
  updateMenu: (id) => `${API_BASE.MENU}/${id}`,

  /**
   * 删除菜单
   * DELETE /api/menu/{id}
   */
  deleteMenu: (id) => `${API_BASE.MENU}/${id}`,

  /**
   * 获取所有角色
   * GET /api/menu/roles
   */
  getRoles: `${API_BASE.MENU}/roles`,

  /**
   * 获取角色的菜单ID列表
   * GET /api/menu/role/{roleId}/menus
   */
  getRoleMenus: (roleId) => `${API_BASE.MENU}/role/${roleId}/menus`,

  /**
   * 为角色分配菜单权限
   * POST /api/menu/assign
   */
  assignMenus: `${API_BASE.MENU}/assign`
}

export default api



// WEBPACK FOOTER //
// ./src/utils/api.js